import { useRef, useState } from 'react';
import { ChevronLeft, Smile, Image as ImageIcon, Trash2 } from 'lucide-react';
import { ListItem } from '@/components/ui';
import { SubPageHeader } from '@/components/ui/SubPageHeader';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { useRegisterOverlay } from '@/stores/overlayStore';

/* Applied at the call site rather than inside `ListItem`, which is shared
   across surfaces that have each been QA'd separately. Restyling the
   primitive to satisfy one screen silently moves all of them — that is
   exactly how the Select placeholder regression happened (YAR-159). */

/** 1px #e5e5e5 around each option — `border` is that token (YAR-158). */
const OPTION_BORDER = 'border border-border rounded-lg';
/** 24×24, not the 20×20 these were rendering at. */
const OPTION_ICON = 'w-6 h-6 text-icon-default';
/** `text/headings` (#000), a step above body copy. */
const OPTION_TITLE = 'text-text-headings';

export interface ChangeProfileImageProps {
  onBack: () => void;
  onChooseEmoji: () => void;
  /**
   * Called with the selected File after the user picks one. Caller is
   * responsible for the actual upload (we stay a dumb visual primitive so
   * callers control which target the image attaches to). Must throw to
   * surface an error; return value is ignored.
   */
  onUpload?: (file: File) => Promise<void>;
  /**
   * Called when the user taps "Remove photo". Only rendered when
   * `hasImage` is true.
   */
  onRemove?: () => Promise<void>;
  /**
   * Current avatar state, rendered as a preview above the options.
   *
   * Briefly removed: the frame draws the options list directly under the
   * heading, and QA read that as "image preview shouldn't be displayed"
   * (YAR-158) pending @kelvin's confirmation. That confirmation came back the
   * other way — "let's keep the image preview in the current implementation"
   * — so the frame loses to the standup call and the preview stays.
   *
   * Resolve it through `ownAvatarProps`, as the callers do. A Google-sourced
   * avatar lives on the session rather than in `avatar_image_path`, so
   * building it from the stored fields alone shows the user the default
   * unicorn instead of their own photo (YAR-302).
   */
  preview?: {
    imagePath?: string | null;
    /** Local preview URL (e.g. `blob:` from a file just picked but not yet uploaded). */
    directUrl?: string | null;
    emoji?: string | null;
    color?: string | null;
    initials?: string;
  };
  /** Whether the target currently has an uploaded image. Drives the Remove row. */
  hasImage?: boolean;
  /**
   * `headline` (default): chevron-only bar with a 28px page headline under
   * it — the onboarding shape YAR-158 asked for. `subpage`: the Settings
   * sub-page bar, a 20px title centred inline with the chevron, which is how
   * Settings' Figma draws this screen (YAR-143, YAR-145). The two flows
   * disagree in Figma, so the caller picks rather than one silently losing.
   */
  headerVariant?: 'headline' | 'subpage';
}

const ACCEPTED_MIME = 'image/png,image/jpeg,image/webp';

export function ChangeProfileImage({
  onBack,
  onChooseEmoji,
  onUpload,
  onRemove,
  preview,
  hasImage = false,
  headerVariant = 'headline',
}: ChangeProfileImageProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mounted only while shown, and it covers the whole screen: hide and
  // pause the bottom toast slot underneath it (YAR-476).
  useRegisterOverlay(true);

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset the input so selecting the same file twice re-triggers onChange.
    event.target.value = '';
    if (!file || !onUpload) return;
    setError(null);
    setBusy(true);
    try {
      await onUpload(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (!onRemove) return;
    setError(null);
    setBusy(true);
    try {
      await onRemove();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove photo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200] bg-background overflow-y-auto flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby={
        headerVariant === 'headline' ? 'change-image-heading' : undefined
      }
      aria-label={
        headerVariant === 'subpage' ? 'Change profile image' : undefined
      }
    >
      {headerVariant === 'subpage' ? (
        <SubPageHeader
          title="Change profile image"
          onBack={() => !busy && onBack()}
        />
      ) : (
        <>
          {/* Header carries the back chevron only; the title is the page
              headline below it — the shape every other onboarding sub-screen
              uses (see AddEditCollaboratorForm). This screen kept a 16px
              centred header-bar title, which QA filed as "title format ...
              not updated, doesn't match figma" (YAR-158). */}
          <div className="sticky top-0 z-[100] bg-background">
            <div className="flex items-center px-4 py-3">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center justify-center rounded-md p-1 text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
                aria-label="Go back"
                disabled={busy}
              >
                <ChevronLeft size={24} />
              </button>
            </div>
          </div>

          {/* Sentence case per the Dev Notes global rule + Kelvin's Jul 21
              note on YAR-158 — it shipped as "Change Profile Image". */}
          <h1
            id="change-image-heading"
            className="px-5 pt-2 text-[28px] leading-8 font-bold text-text-hero"
          >
            Change profile image
          </h1>
        </>
      )}

      {/* Kept per @kelvin's call on YAR-158 ("let's keep the image preview in
          the current implementation"), which reversed the earlier read of the
          frame. See `preview` on the props for why it must resolve through
          `ownAvatarProps`. */}
      {preview && (
        <div className="flex justify-center py-6">
          <ProfileAvatar
            imagePath={preview.imagePath}
            directUrl={preview.directUrl}
            emoji={preview.emoji}
            color={preview.color}
            initials={preview.initials}
            size="xl"
          />
        </div>
      )}

      <div className="px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] flex flex-col gap-2">
        {/* `Image`, not `Upload`. The frame draws a picture glyph (framed
            rectangle with a mountain); we shipped the upload arrow, which QA
            filed as "upload photo icon doesn't match figma" (YAR-158).
            Aliased on import because `Image` shadows the DOM global. */}
        {onUpload && (
          <ListItem
            className={OPTION_BORDER}
            icon={<ImageIcon className={OPTION_ICON} />}
            title={busy ? 'Uploading…' : 'Upload photo'}
            titleClassName={OPTION_TITLE}
            onClick={() => !busy && fileInputRef.current?.click()}
          />
        )}

        <ListItem
          className={OPTION_BORDER}
          icon={<Smile className={OPTION_ICON} />}
          title="Choose emoji"
          titleClassName={OPTION_TITLE}
          onClick={onChooseEmoji}
        />

        {hasImage && onRemove && (
          <ListItem
            className={OPTION_BORDER}
            icon={<Trash2 className="w-6 h-6 text-danger" />}
            title={busy ? 'Removing…' : 'Remove photo'}
            titleClassName={OPTION_TITLE}
            onClick={handleRemove}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_MIME}
          className="hidden"
          onChange={handleFileChange}
          aria-hidden="true"
        />

        {error && (
          <div
            role="alert"
            className="mt-2 rounded-md bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger"
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
